import { memo, useState, useMemo } from 'react';
import type { AnswerOptionItem } from 'screens/settings/DueDiligenceCategoryEditor/types';
import {
  DueDiligenceStatusColor,
  ConditionGroup,
  AnswerOptionConditionType,
  isConditionGroup,
} from 'shared/types/due-diligence';
import { Card, CardBody, Button } from '@heroui/react';
import { Flask } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface ConditionSimulatorProps {
  answerOptions: AnswerOptionItem[];
}

/**
 * Status color to Tailwind classes mapping
 */
const STATUS_COLORS: Record<DueDiligenceStatusColor, string> = {
  [DueDiligenceStatusColor.GREEN]: 'bg-green-500',
  [DueDiligenceStatusColor.ORANGE]: 'bg-orange-500',
  [DueDiligenceStatusColor.RED]: 'bg-red-500',
  [DueDiligenceStatusColor.TRANSPARENT]: 'bg-transparent border border-gray-300',
};

/**
 * Predefined test scenarios
 */
type ScenarioType = 'none' | 'valid' | 'expired' | 'missing';

interface Scenario {
  id: ScenarioType;
  label: string;
  description: string;
}

const SCENARIOS: Scenario[] = [
  {
    id: 'valid',
    label: 'Document aanwezig en geldig',
    description: 'Simuleert een geldig document dat nog niet binnenkort verloopt',
  },
  {
    id: 'expired',
    label: 'Document aanwezig maar verlopen',
    description: 'Simuleert een document dat verlopen is of binnenkort verloopt',
  },
  {
    id: 'missing',
    label: 'Document ontbreekt',
    description: 'Simuleert een situatie waarin het vereiste document niet aanwezig is',
  },
  {
    id: 'none',
    label: 'Geen scenario',
    description: 'Reset naar neutrale weergave',
  },
];

/**
 * ConditionSimulator - "Wat als...?" panel
 *
 * Allows users to simulate different scenarios to see which answer options would match.
 * This is a CLIENT-SIDE ONLY simulation using heuristics, not actual condition evaluation.
 */
export const ConditionSimulator = memo(({ answerOptions }: ConditionSimulatorProps) => {
  const [selectedScenario, setSelectedScenario] = useState<ScenarioType>('none');

  /**
   * Get matching answer options for the selected scenario
   */
  const matchingOptionIds = useMemo(() => {
    try {
      if (selectedScenario === 'none') {
        return new Set<number | undefined>();
      }

      const matches = new Set<number | undefined>();

      for (const option of answerOptions) {
        const isMatch = doesOptionMatchScenario(option, selectedScenario);
        if (isMatch) {
          matches.add(option.id);
        }
      }

      return matches;
    } catch (error) {
      console.error('Error calculating matching options:', error);
      return new Set<number | undefined>();
    }
  }, [answerOptions, selectedScenario]);

  return (
    <Card className="w-full">
      <CardBody className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Flask size={20} weight="duotone" className="text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Wat als...?</h3>
        </div>

        <p className="text-sm text-gray-600">
          Test verschillende scenario's om te zien welke antwoordoptie zou worden geselecteerd
        </p>

        {/* Scenario buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SCENARIOS.map((scenario) => (
            <Button
              key={scenario.id}
              variant={selectedScenario === scenario.id ? 'solid' : 'bordered'}
              color={selectedScenario === scenario.id ? 'primary' : 'default'}
              className="h-auto py-3 px-4 justify-start text-left"
              onClick={() => setSelectedScenario(scenario.id)}
            >
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">{scenario.label}</span>
                <span className="text-xs opacity-70 font-normal">
                  {scenario.description}
                </span>
              </div>
            </Button>
          ))}
        </div>

        {/* Answer options list */}
        <div className="space-y-2 pt-4 border-t border-gray-200">
          <p className="text-sm font-medium text-gray-700">
            Resultaat:
          </p>

          {answerOptions.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">
              Geen antwoordopties geconfigureerd
            </p>
          ) : (
            <div className="space-y-2">
              {answerOptions.map((option, index) => {
                const isMatching = matchingOptionIds.has(option.id);
                const isActive = selectedScenario !== 'none';

                return (
                  <AnswerOptionPreview
                    key={option.id || index}
                    option={option}
                    isMatching={isMatching}
                    isActive={isActive}
                  />
                );
              })}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
});

ConditionSimulator.displayName = 'ConditionSimulator';

/**
 * AnswerOptionPreview - Single answer option display
 */
interface AnswerOptionPreviewProps {
  option: AnswerOptionItem;
  isMatching: boolean;
  isActive: boolean;
}

const AnswerOptionPreview = memo(({ option, isMatching, isActive }: AnswerOptionPreviewProps) => {
  try {
    const dotColorClass = STATUS_COLORS[option.statusColor];

    return (
      <div
        className={cn(
          "flex items-center gap-3 p-3 rounded-lg border transition-all",
          isActive && isMatching && "ring-2 ring-blue-500 bg-blue-50 border-blue-300",
          isActive && !isMatching && "opacity-40",
          !isActive && "border-gray-200 bg-white"
        )}
      >
        {/* Status dot */}
        <div
          className={cn(
            "w-3 h-3 rounded-full flex-shrink-0",
            dotColorClass
          )}
          aria-label={`Status: ${option.label}`}
        />

        {/* Option details */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {option.label}
          </p>
          {option.value && (
            <p className="text-xs text-gray-500 truncate">
              Waarde: {option.value}
            </p>
          )}
        </div>

        {/* Match indicator */}
        {isActive && isMatching && (
          <div className="flex-shrink-0">
            <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">
              Match
            </span>
          </div>
        )}
      </div>
    );
  } catch (error) {
    console.error('Error rendering AnswerOptionPreview:', error);
    return null;
  }
});

AnswerOptionPreview.displayName = 'AnswerOptionPreview';

/**
 * Helper function: Check if a condition tree contains a specific condition type
 */
function hasConditionType(
  root: ConditionGroup | null,
  type: AnswerOptionConditionType
): boolean {
  if (!root) {
    return false;
  }

  try {
    for (const child of root.children) {
      if (isConditionGroup(child)) {
        // Recursively check nested groups
        if (hasConditionType(child, type)) {
          return true;
        }
      } else {
        // Check if this condition matches the type
        if (child.type === type) {
          return true;
        }
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking condition type:', error);
    return false;
  }
}

/**
 * Helper function: Check if a FILE_EXISTS condition with fileExists=false exists
 */
function hasFileMissingCondition(root: ConditionGroup | null): boolean {
  if (!root) {
    return false;
  }

  try {
    for (const child of root.children) {
      if (isConditionGroup(child)) {
        // Recursively check nested groups
        if (hasFileMissingCondition(child)) {
          return true;
        }
      } else {
        // Check if this is a FILE_EXISTS condition with fileExists=false
        if (
          child.type === AnswerOptionConditionType.FILE_EXISTS &&
          child.fileExists === false
        ) {
          return true;
        }
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking file missing condition:', error);
    return false;
  }
}

/**
 * Heuristic: Determine if an answer option matches a scenario
 */
function doesOptionMatchScenario(
  option: AnswerOptionItem,
  scenario: ScenarioType
): boolean {
  try {
    switch (scenario) {
      case 'valid':
        // Match options with GREEN status
        return option.statusColor === DueDiligenceStatusColor.GREEN;

      case 'expired':
        // Match options with ORANGE or RED status that have expiration conditions
        if (
          option.statusColor === DueDiligenceStatusColor.ORANGE ||
          option.statusColor === DueDiligenceStatusColor.RED
        ) {
          // Check if there's an EXPIRATION_DATE condition
          return hasConditionType(option.conditionRoot, AnswerOptionConditionType.EXPIRATION_DATE);
        }
        return false;

      case 'missing':
        // Match options with RED status and FILE_EXISTS conditions (fileExists=false)
        // OR options with no conditions and RED status
        if (option.statusColor === DueDiligenceStatusColor.RED) {
          // Check if there's a file missing condition or no conditions at all
          return (
            hasFileMissingCondition(option.conditionRoot) ||
            !option.conditionRoot ||
            (option.conditionRoot.children && option.conditionRoot.children.length === 0)
          );
        }
        return false;

      case 'none':
      default:
        return false;
    }
  } catch (error) {
    console.error('Error matching option to scenario:', error);
    return false;
  }
}
